/* ============================================================
   NOELMA TELES — NATIVE BUNDLE CART
   File: assets/nt-bundle-cart.js

   Handles:
   - Remove an entire bundle in one action
   - Uses Shopify Ajax Cart API
   - Refreshes Palo Alto cart drawer after removal
   - Leaves normal cart lines untouched
   ============================================================ */

(() => {
  'use strict';

  if (window.NTBundleCartInitialized) return;
  window.NTBundleCartInitialized = true;

  const ROOT = window.Shopify?.routes?.root || '/';

  const route = (path) => {
    return `${ROOT}${path}`.replace(/([^:]\/)\/+/g, '$1');
  };

  const getUpdateUrl = () => {
    return window.theme?.routes?.cart_update_url || route('cart/update.js');
  };

  const getCartUrl = () => {
    return window.theme?.routes?.cart_url || route('cart');
  };

  const safeJson = async (response) => {
    try {
      return await response.json();
    } catch (_error) {
      return {};
    }
  };

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/["\\]/g, '\\$&');
  };

  const getBundleItems = (bundleId) => {
    const safeId = escapeSelector(bundleId);

    return Array.from(
      document.querySelectorAll(
        `[data-nt-bundle-item][data-nt-bundle-id="${safeId}"]`
      )
    );
  };

  const getBundleHeaders = (bundleId) => {
    const safeId = escapeSelector(bundleId);

    return Array.from(
      document.querySelectorAll(
        `[data-nt-bundle-header][data-nt-bundle-id="${safeId}"]`
      )
    );
  };

  const setLoading = (bundleId, loading) => {
    getBundleHeaders(bundleId).forEach((header) => {
      header.classList.toggle('is-loading', loading);

      const button = header.querySelector('[data-nt-bundle-remove]');

      if (!button) return;

      if (loading) {
        if (!button.dataset.ntOriginalText) {
          button.dataset.ntOriginalText = button.textContent.trim();
        }

        button.disabled = true;
        button.textContent = 'Removendo...';
      } else {
        button.disabled = false;
        button.textContent =
          button.dataset.ntOriginalText || 'Remover kit';
      }
    });

    getBundleItems(bundleId).forEach((item) => {
      item.classList.toggle('is-loading', loading);
    });
  };

  const clearError = (bundleId) => {
    getBundleHeaders(bundleId).forEach((header) => {
      header
        .querySelectorAll('.nt-bundle-cart__error')
        .forEach((error) => error.remove());
    });
  };

  const showError = (bundleId, message) => {
    getBundleHeaders(bundleId).forEach((header) => {
      let error = header.querySelector('.nt-bundle-cart__error');

      if (!error) {
        error = document.createElement('div');
        error.className = 'nt-bundle-cart__error';
        error.setAttribute('role', 'alert');
        header.appendChild(error);
      }

      error.textContent = message;
    });
  };

  const collectUpdates = (button, bundleId) => {
    const updates = {};
    const form = button.closest('[data-nt-bundle-remove-form]');

    /*
     * Primary source: hidden inputs rendered by Liquid.
     * This gives us the exact Shopify cart line keys.
     */
    if (form) {
      form
        .querySelectorAll('input[name^="updates["]')
        .forEach((input) => {
          const match = input.name.match(/^updates\[(.+)\]$/);

          if (match?.[1]) {
            updates[match[1]] = 0;
          }
        });
    }

    /*
     * Fallback: read line keys from data-item.
     */
    if (!Object.keys(updates).length) {
      getBundleItems(bundleId).forEach((item) => {
        const key = item.dataset.item;

        if (key) {
          updates[key] = 0;
        }
      });
    }

    return updates;
  };

  const refreshAfterRemoval = () => {
    const drawer = document.querySelector(
      'cart-element[data-cart-drawer], [data-cart-drawer]'
    );

    if (drawer) {
      document.dispatchEvent(
        new CustomEvent('theme:cart:refresh', {
          bubbles: true
        })
      );

      return;
    }

    window.location.href = getCartUrl();
  };

  const removeBundle = async (button) => {
    const bundleId = String(button.dataset.ntBundleId || '').trim();

    if (!bundleId) return;

    clearError(bundleId);

    const updates = collectUpdates(button, bundleId);

    if (!Object.keys(updates).length) {
      showError(
        bundleId,
        'Não foi possível identificar os itens deste kit.'
      );
      return;
    }

    setLoading(bundleId, true);

    try {
      const response = await fetch(getUpdateUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates })
      });

      const result = await safeJson(response);

      if (!response.ok || result?.status) {
        throw new Error(
          result?.description ||
          result?.message ||
          'Não foi possível remover o kit.'
        );
      }

      refreshAfterRemoval();
    } catch (error) {
      console.error('[NT Bundle Cart] Remove error:', error);

      setLoading(bundleId, false);

      showError(
        bundleId,
        error?.message ||
        'Não foi possível remover o kit. Tente novamente.'
      );
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nt-bundle-remove]');

    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    removeBundle(button);
  });

  document.documentElement.classList.add('nt-bundle-cart-ready');
})();
